/**
 * Credit Recharge Dialog Component
 *
 * 크레딧 구매를 위한 통합 다이얼로그.
 * - 한국 사용자: NicePay Lite (KRW) - goPay() 방식
 * - 해외 사용자: Lemon Squeezy (USD)
 */
import { Loader2Icon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useFetcher } from "react-router";

import type { CreditPackage, PaymentConfig } from "../types/packages";

/**
 * NicePay Lite Sign API 응답 타입
 *
 * /api/payments/nice/sign 엔드포인트가 반환하는 서명 데이터.
 * 클라이언트는 이 데이터를 hidden form에 채워 goPay() 호출.
 */
interface NicePaySignResponse {
  mid?: string;
  ediDate?: string;
  moid?: string;
  signData?: string;
  amt?: string;
  goodsName?: string;
  error?: string;
}

/**
 * Lemon Squeezy 체크아웃 API 응답 타입
 */
interface CheckoutResponse {
  checkoutUrl?: string;
  error?: string;
}

/**
 * Lemon Squeezy 이벤트 데이터 타입
 */
interface LemonSqueezyEventData {
  event: string;
  data?: unknown;
}

/**
 * 전역 SDK 타입 선언
 *
 * NicePay Lite SDK: nicepaySubmit(), nicepayClose(), goPay()
 * Lemon Squeezy SDK: createLemonSqueezy(), LemonSqueezy
 */
declare global {
  interface Window {
    // NicePay Lite 콜백 함수 (PC 결제 흐름)
    nicepaySubmit: () => void;
    nicepayClose: () => void;
    // NicePay Lite SDK가 제공하는 결제창 열기 함수
    goPay: (form: HTMLFormElement) => void;
    // Lemon Squeezy
    createLemonSqueezy?: () => void;
    LemonSqueezy?: {
      Url: { Open: (url: string) => void };
      Setup: (options: { eventHandler: (data: LemonSqueezyEventData) => void }) => void;
    };
  }
}

interface CreditRechargeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: PaymentConfig;
  currentCredits?: number;
  requiredCredits?: number;
}

/**
 * 통화별 가격 포맷
 */
function formatPrice(amount: number, currency: "KRW" | "USD"): string {
  if (currency === "KRW") {
    return new Intl.NumberFormat("ko-KR", {
      style: "currency",
      currency: "KRW",
    }).format(amount);
  }
  return `$${amount.toFixed(2)}`;
}

export function CreditRechargeDialog({
  open,
  onOpenChange,
  config,
  currentCredits = 0,
  requiredCredits,
}: CreditRechargeDialogProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // NicePay Lite SDK 로드 완료 여부
  const nicePayReadyRef = useRef(false);
  // goPay() 결제창이 열려있는 동안 다이얼로그 닫기 방지
  const goPaying = useRef(false);

  // NicePay Lite sign API용 fetcher (LS fetcher와 분리)
  const niceSignFetcher = useFetcher<NicePaySignResponse>();
  // Lemon Squeezy checkout fetcher
  const fetcher = useFetcher<CheckoutResponse>();

  const { packages, isKoreanUser, nicePayMID, userId, userEmail, userName } = config;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (open) {
      setErrorMessage(null);
    }
  }, [open]);

  /**
   * NicePay Lite 결제에 사용할 hidden form을 document.body에 생성/관리
   *
   * Dialog 포털 외부에 배치해야 goPay() 결제창이 활성화된 동안
   * Dialog 리렌더 시에도 form이 유지됩니다.
   */
  useEffect(() => {
    if (!isKoreanUser) return;

    const form = document.createElement("form");
    form.id = "nicepay-form";
    form.method = "POST";
    form.action = "/api/payments/nice/confirm";
    form.acceptCharset = "euc-kr";
    // display:none 사용 금지 - NicePay SDK가 form element 기준으로
    // 결제창 background dim 처리를 하므로 레이아웃에 존재해야 함.
    // 모든 input이 type="hidden"이므로 시각적 영향 없음.
    form.style.overflow = "hidden";
    form.style.height = "0";

    // NicePay Lite 결제 요청에 필요한 hidden input 필드 생성
    // NpLang, GoodsCl, TransType, ReqReserved는 NicePay Lite SDK 필수 필드
    const fields = [
      "PayMethod",
      "GoodsName",
      "Amt",
      "MID",
      "EdiDate",
      "Moid",
      "SignData",
      "BuyerName",
      "BuyerEmail",
      "BuyerTel",
      "ReturnURL",
      "CharSet",
      "NpLang",
      "GoodsCl",
      "TransType",
      "ReqReserved",
    ];
    fields.forEach((name) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    return () => {
      if (document.body.contains(form)) {
        document.body.removeChild(form);
      }
    };
  }, [isKoreanUser]);

  /**
   * NicePay Lite PC 흐름 콜백 등록
   *
   * nicepaySubmit: NicePay SDK가 인증 완료 후 호출 → form.submit()으로 confirm 엔드포인트로 전송
   * nicepayClose: 사용자가 결제창을 닫을 때 호출 → 로딩 상태 초기화
   *
   * form.submit()을 사용하는 이유: confirm 엔드포인트가 302 리다이렉트를 반환하기 때문.
   * fetch()는 수동 리다이렉트 처리가 필요하고 모바일 흐름과 동작이 달라짐.
   */
  useEffect(() => {
    if (!isKoreanUser) return;

    window.nicepaySubmit = () => {
      const f = document.getElementById("nicepay-form") as HTMLFormElement | null;
      if (f) {
        f.submit();
      }
    };

    window.nicepayClose = () => {
      setLoadingPlan(null);
      goPaying.current = false;
    };

    return () => {
      // cleanup - 타입 호환을 위해 빈 함수로 교체
      window.nicepaySubmit = () => {};
      window.nicepayClose = () => {};
    };
  }, [isKoreanUser]);

  /**
   * 지역에 따라 적절한 SDK 로드
   */
  useEffect(() => {
    if (!open) return;

    if (isKoreanUser && nicePayMID && userId) {
      // NicePay Lite SDK 로드
      if (nicePayReadyRef.current || typeof window.goPay === "function") {
        nicePayReadyRef.current = true;
        setIsLoading(false);
        return;
      }

      // NicePay SDK의 receiveMessageValue 함수에는 eval()에 try-catch가 없어서
      // React Router/Vite HMR의 postMessage가 SyntaxError를 발생시키고
      // 실제 결제 결과 메시지 처리를 방해함.
      // addEventListener를 래핑하여 message 리스너에 필터 + try-catch 추가.
      // goPay() 호출 시점에 리스너가 등록되므로 래핑을 유지해야 함.
      const origAEL = window.addEventListener;

      window.addEventListener = function (
        this: EventTarget,
        type: string,
        listener: EventListenerOrEventListenerObject | null,
        options?: boolean | AddEventListenerOptions,
      ) {
        if (type === "message" && typeof listener === "function") {
          const origFn = listener;
          const safeFn = function (this: unknown, e: Event) {
            const me = e as MessageEvent;
            // HMR/DevTools의 object 메시지만 필터링
            const origin = me.origin || "";
            const isNicePay = origin.includes("nicepay.co.kr");
            const isString = typeof me.data === "string";

            if (
              !isNicePay &&
              !isString &&
              typeof me.data === "object" &&
              me.data !== null
            ) {
              return;
            }
            try {
              origFn.call(this, e);
            } catch {
              // NicePay SDK eval() 오류 무시
            }
          };
          return origAEL.call(
            this,
            type,
            safeFn as EventListener,
            options,
          );
        }
        return origAEL.call(this, type, listener!, options);
      } as typeof window.addEventListener;

      const script = document.createElement("script");
      script.src = "https://pg-web.nicepay.co.kr/v3/common/js/nicepay-pgweb.js";
      script.async = true;

      script.onload = () => {
        nicePayReadyRef.current = true;
        setIsLoading(false);
      };

      script.onerror = () => {
        window.addEventListener = origAEL;
        setIsLoading(false);
        setErrorMessage("결제 모듈을 불러올 수 없습니다. 페이지를 새로고침해주세요.");
      };

      document.body.appendChild(script);
      return () => {
        window.addEventListener = origAEL;
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    } else if (!isKoreanUser) {
      // Lemon Squeezy SDK 로드
      if (window.LemonSqueezy) {
        setupLemonSqueezyEventHandler();
        setIsLoading(false);
        return;
      }

      const existingScript = document.querySelector(
        'script[src="https://app.lemonsqueezy.com/js/lemon.js"]'
      );
      if (existingScript) {
        if (window.createLemonSqueezy) {
          window.createLemonSqueezy();
        }
        setupLemonSqueezyEventHandler();
        setIsLoading(false);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://app.lemonsqueezy.com/js/lemon.js";
      script.defer = true;
      document.body.appendChild(script);

      script.onload = () => {
        if (window.createLemonSqueezy) {
          window.createLemonSqueezy();
        }
        setupLemonSqueezyEventHandler();
        setIsLoading(false);
      };

      script.onerror = () => {
        setIsLoading(false);
      };
    }
  }, [open, isKoreanUser, nicePayMID, userId]);

  /**
   * Lemon Squeezy 체크아웃 성공 이벤트 핸들러 설정
   */
  const setupLemonSqueezyEventHandler = () => {
    window.LemonSqueezy?.Setup({
      eventHandler: (data: LemonSqueezyEventData) => {
        if (data.event === "Checkout.Success") {
          window.location.href = "/payments/success";
        }
      },
    });
  };

  /**
   * NicePay Lite sign API 응답 처리
   *
   * /api/payments/nice/sign에서 { mid, ediDate, moid, signData, amt, goodsName }를 받아
   * document.body의 hidden form을 채우고 goPay()를 호출합니다.
   */
  useEffect(() => {
    if (!niceSignFetcher.data) return;

    if (niceSignFetcher.data.error) {
      setLoadingPlan(null);
      setErrorMessage(niceSignFetcher.data.error || "결제 준비에 실패했습니다. 다시 시도해주세요.");
      return;
    }

    const { mid, ediDate, moid, signData, amt, goodsName } = niceSignFetcher.data;

    if (!mid || !ediDate || !moid || !signData || !amt || !goodsName) {
      setLoadingPlan(null);
      return;
    }

    const form = document.getElementById("nicepay-form") as HTMLFormElement | null;
    if (!form) {
      setLoadingPlan(null);
      return;
    }

    const appUrl = window.location.origin;

    // hidden form 필드 채우기
    const setField = (name: string, value: string) => {
      const input = form.querySelector<HTMLInputElement>(`input[name="${name}"]`);
      if (input) input.value = value;
    };

    setField("PayMethod", "CARD");
    setField("GoodsName", goodsName);
    setField("Amt", amt);
    setField("MID", mid);
    setField("EdiDate", ediDate);
    setField("Moid", moid);
    setField("SignData", signData);
    setField("BuyerName", userName || "");
    setField("BuyerEmail", userEmail || "");
    setField("BuyerTel", "");
    setField("ReturnURL", `${appUrl}/api/payments/nice/confirm`);
    setField("CharSet", "utf-8");
    setField("NpLang", "KO");
    setField("GoodsCl", "1");
    setField("TransType", "0");
    setField("ReqReserved", "");

    // form action도 절대 URL로 설정
    form.action = `${appUrl}/api/payments/nice/confirm`;

    // goPay()로 NicePay Lite 결제창 열기
    if (typeof window.goPay === "function") {
      try {
        goPaying.current = true;
        window.goPay(form);
      } catch {
        // goPay() 내부 오류 시 로딩 상태 복구 (form 파라미터 오류 등)
        goPaying.current = false;
        setLoadingPlan(null);
      }
    } else {
      setLoadingPlan(null);
    }
  }, [niceSignFetcher.data, userName, userEmail]);

  /**
   * Lemon Squeezy 체크아웃 URL 응답 처리
   */
  useEffect(() => {
    if (fetcher.data?.checkoutUrl) {
      if (window.LemonSqueezy?.Url?.Open) {
        try {
          window.LemonSqueezy.Url.Open(fetcher.data.checkoutUrl);
        } catch {
          window.open(fetcher.data.checkoutUrl, "_blank");
        }
      } else {
        window.open(fetcher.data.checkoutUrl, "_blank");
      }
      setLoadingPlan(null);
    } else if (fetcher.data?.error) {
      setLoadingPlan(null);
      setErrorMessage(fetcher.data.error || "결제 연결에 실패했습니다. 다시 시도해주세요.");
    }
  }, [fetcher.data]);

  /**
   * 패키지 선택 처리
   *
   * NicePay Lite 흐름: packageId만 sign 엔드포인트로 전송 (amt는 서버에서 결정)
   * Lemon Squeezy 흐름: variantId를 checkout 엔드포인트로 전송
   */
  const handlePlanClick = (pkg: CreditPackage) => {
    if (loadingPlan || fetcher.state !== "idle" || niceSignFetcher.state !== "idle") return;

    setErrorMessage(null);
    setLoadingPlan(pkg.id);

    if (isKoreanUser && nicePayReadyRef.current && nicePayMID && userId) {
      // NicePay Lite 흐름: 서버에서 SignData 생성 요청
      // 클라이언트는 packageId만 전송 - 가격은 서버에서 결정 (가격 위변조 방지)
      niceSignFetcher.submit(
        { packageId: pkg.id },
        { method: "POST", action: "/api/payments/nice/sign" }
      );
    } else if (!isKoreanUser) {
      // Lemon Squeezy 흐름
      if (!pkg.variantId) {
        setLoadingPlan(null);
        return;
      }
      fetcher.submit(
        { variantId: pkg.variantId },
        { method: "POST", action: "/api/payments/checkout" }
      );
    }
  };

  const handleClose = () => {
    // goPay() 결제창이 열려있는 동안 다이얼로그 닫기 방지
    if (loadingPlan || goPaying.current) return;
    onOpenChange(false);
  };

  if (!open || !mounted) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[250] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#F3F3F1]/90 backdrop-blur-md transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative flex max-h-[90vh] w-full max-w-sm animate-[fadeInUp_0.3s_ease-out] flex-col overflow-hidden rounded-[32px] border border-gray-100 bg-white shadow-[0_32px_64px_rgba(0,0,0,0.15)]">
        {/* Close button */}
        <button
          onClick={handleClose}
          disabled={!!loadingPlan || goPaying.current}
          className="absolute right-6 top-6 z-10 p-2 text-gray-400 transition-colors hover:text-black disabled:opacity-0"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Header */}
        <div className="p-10 pb-2 text-center">
          <h3 className="mb-2 text-2xl font-black uppercase tracking-tight text-black">
            Purchase Credits
          </h3>
          <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400">
            {requiredCredits
              ? `${requiredCredits} 크레딧 필요 • 현재 ${currentCredits} 크레딧`
              : "크레딧을 구매하여 이미지를 생성하세요"}
          </p>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="mx-8 rounded-xl bg-red-50 px-4 py-3 text-center">
            <p className="text-xs font-medium text-red-600">{errorMessage}</p>
          </div>
        )}

        {/* Content */}
        <div className="scrollbar-hide overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center gap-4 py-12">
              <Loader2Icon className="size-8 animate-spin text-gray-400" />
              <span className="text-sm text-gray-400">결제 수단을 불러오는 중...</span>
            </div>
          ) : packages.length === 0 ? (
            <div className="space-y-3 p-8">
              <p className="text-center text-sm text-gray-500">
                결제 상품이 설정되지 않았습니다.
              </p>
              <p className="text-center font-mono text-[10px] text-gray-400">
                관리자에게 문의해주세요.
              </p>
            </div>
          ) : (
            <div className="space-y-3 p-8">
              {packages.map((pkg) => (
                <button
                  key={pkg.id}
                  type="button"
                  onClick={() => handlePlanClick(pkg)}
                  disabled={!!loadingPlan}
                  className="block w-full text-left"
                >
                  <div
                    className={`flex w-full items-center justify-between rounded-2xl border p-5 transition-all ${
                      pkg.popular
                        ? "border-black bg-black text-white"
                        : "border-gray-200 bg-white text-black hover:border-black"
                    } ${loadingPlan === pkg.id ? "ring-2 ring-blue-500" : ""} ${loadingPlan && loadingPlan !== pkg.id ? "opacity-50" : ""}`}
                  >
                    <div>
                      <div
                        className={`mb-1 text-[10px] font-bold uppercase tracking-widest ${
                          pkg.popular ? "text-gray-400" : "text-gray-400"
                        }`}
                      >
                        {pkg.name}
                      </div>
                      <div className="text-xl font-black">{pkg.credits} Credits</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm font-bold">
                        {formatPrice(pkg.price, pkg.currency)}
                      </div>
                      {loadingPlan === pkg.id && (
                        <div className="mt-1 animate-pulse text-[9px] font-bold text-blue-400">
                          결제 연결 중...
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-8 pt-2 text-center">
          <button
            onClick={handleClose}
            disabled={!!loadingPlan || goPaying.current}
            className="text-[10px] font-black uppercase tracking-widest text-gray-300 transition-colors hover:text-black disabled:opacity-0"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
